/**
 * PLANTILLAS del chat de Gustavo. (08/09/2026)
 *
 * Un mensaje con la estructura ya armada. Los campos con opciones cerradas (tipo,
 * operación, moneda, dónde publicar, estado) se eligen con un toque ANTES de insertar,
 * así en el texto quedan solo los datos que hay que escribir de verdad: nombre, teléfono,
 * dirección, precio. Cuantos menos corchetes le queden al agente, mejor: menos olvidos,
 * menos repreguntas de Gustavo, menos tokens.
 */
export type CampoDePlantilla =
  /** Lo escribe el agente: queda como [placeholder] en el texto. */
  | { rotulo: string; tipo: 'libre'; placeholder: string; opcional?: boolean }
  /** Una opción de varias: se elige con un toque, entra ya resuelta. */
  | { rotulo: string; tipo: 'opcion'; opciones: readonly string[]; defecto: string }
  /** Varias a la vez (dónde publicar). */
  | { rotulo: string; tipo: 'multi'; opciones: readonly string[]; defecto: readonly string[] };

export interface PlantillaDeGustavo {
  id: string;
  titulo: string;
  /** Para qué sirve, en una línea. */
  descripcion: string;
  /**
   * La ORDEN, en imperativo, que va primero: "Cargá esta propiedad y publicala:". Sin esto el
   * mensaje son datos sueltos y Gustavo tiene que adivinar qué hacer con ellos; con la orden
   * arriba elige la tool de una. (pedido del user, 08/09/2026)
   */
  orden: string;
  campos: readonly CampoDePlantilla[];
}

/** Lo elegido para los campos cerrados, por rótulo. Lo que falta usa el defecto. */
export type Elecciones = Record<string, string | readonly string[]>;

const LO_ARMA_GUSTAVO = 'la arma Gustavo';

export const PLANTILLAS_DE_GUSTAVO: readonly PlantillaDeGustavo[] = [
  {
    id: 'cargar-propiedad',
    titulo: 'Cargar una propiedad',
    descripcion: 'Crea la ficha y la publica en un solo mensaje.',
    orden: 'Cargá esta propiedad en el CRM y publicala:',
    campos: [
      { rotulo: 'Propietario',     tipo: 'libre',  placeholder: 'nombre y teléfono, o el cliente que ya está en el CRM' },
      { rotulo: 'Tipo',            tipo: 'opcion', opciones: ['Casa', 'Apartamento', 'Terreno', 'Campo', 'Chacra', 'Local', 'Oficina', 'Garaje'], defecto: 'Apartamento' },
      { rotulo: 'Operación',       tipo: 'opcion', opciones: ['Venta', 'Alquiler', 'Alquiler temporal'], defecto: 'Venta' },
      { rotulo: 'Ubicación',       tipo: 'libre',  placeholder: 'dirección y barrio' },
      { rotulo: 'Precio',          tipo: 'libre',  placeholder: 'monto' },
      { rotulo: 'Moneda',          tipo: 'opcion', opciones: ['USD', 'UYU'], defecto: 'USD' },
      { rotulo: 'Superficie',      tipo: 'libre',  placeholder: 'm² totales / m² cubiertos' },
      { rotulo: 'Características', tipo: 'libre',  placeholder: 'dormitorios, baños, garaje, otras' },
      { rotulo: 'Título',          tipo: 'opcion', opciones: [LO_ARMA_GUSTAVO, 'lo escribo yo'], defecto: LO_ARMA_GUSTAVO },
      { rotulo: 'Descripción',     tipo: 'opcion', opciones: [LO_ARMA_GUSTAVO, 'la escribo yo'], defecto: LO_ARMA_GUSTAVO },
      { rotulo: 'Fotos',           tipo: 'opcion', opciones: ['adjuntas', 'las mando después'], defecto: 'adjuntas' },
      { rotulo: 'Publicar en',     tipo: 'multi',  opciones: ['Web', 'MercadoLibre', 'Infocasas'], defecto: ['Web', 'MercadoLibre'] },
      { rotulo: 'Estado',          tipo: 'opcion', opciones: ['Activa', 'Inactiva'], defecto: 'Activa' },
    ],
  },
  {
    id: 'cargar-cliente',
    titulo: 'Cargar un cliente',
    descripcion: 'Alta del cliente con lo que busca: ficha e interés juntos.',
    orden: 'Creá este cliente y anotale lo que busca:',
    campos: [
      { rotulo: 'Cliente',     tipo: 'libre',  placeholder: 'nombre y apellido' },
      { rotulo: 'Teléfono',    tipo: 'libre',  placeholder: 'teléfono' },
      { rotulo: 'Email',       tipo: 'libre',  placeholder: 'email', opcional: true },
      { rotulo: 'Busca',       tipo: 'opcion', opciones: ['Comprar', 'Alquilar'], defecto: 'Comprar' },
      { rotulo: 'Tipo',        tipo: 'opcion', opciones: ['Apartamento', 'Casa', 'Terreno', 'Local', 'Cualquiera'], defecto: 'Apartamento' },
      { rotulo: 'Zona',        tipo: 'libre',  placeholder: 'barrios o ciudad' },
      { rotulo: 'Presupuesto', tipo: 'libre',  placeholder: 'hasta cuánto' },
      { rotulo: 'Moneda',      tipo: 'opcion', opciones: ['USD', 'UYU'], defecto: 'USD' },
      { rotulo: 'Necesita',    tipo: 'libre',  placeholder: 'dormitorios, garaje, otras', opcional: true },
    ],
  },
  {
    id: 'agendar-visita',
    titulo: 'Agendar una visita',
    descripcion: 'Queda el evento, el recordatorio de seguimiento y el interés del cliente.',
    orden: 'Agendame esta visita y dejame el seguimiento armado:',
    campos: [
      { rotulo: 'Visita con', tipo: 'libre',  placeholder: 'nombre del cliente' },
      { rotulo: 'Propiedad',  tipo: 'libre',  placeholder: 'código o dirección' },
      { rotulo: 'Día',        tipo: 'libre',  placeholder: 'día' },
      { rotulo: 'Hora',       tipo: 'libre',  placeholder: 'hora' },
      { rotulo: 'Encuentro',  tipo: 'opcion', opciones: ['en la propiedad', 'en la oficina', 'otro lugar'], defecto: 'en la propiedad' },
    ],
  },
  {
    id: 'registrar-captacion',
    titulo: 'Registrar una captación',
    descripcion: 'Un propietario que quiere vender o alquilar, al tablero de captaciones.',
    orden: 'Registrá esta captación en mi tablero:',
    campos: [
      { rotulo: 'Propietario',  tipo: 'libre',  placeholder: 'nombre y teléfono' },
      { rotulo: 'Propiedad',    tipo: 'libre',  placeholder: 'tipo y dirección o barrio' },
      { rotulo: 'Operación',    tipo: 'opcion', opciones: ['Venta', 'Alquiler'], defecto: 'Venta' },
      { rotulo: 'Precio que pide', tipo: 'libre', placeholder: 'monto', opcional: true },
      { rotulo: 'Moneda',       tipo: 'opcion', opciones: ['USD', 'UYU'], defecto: 'USD' },
      { rotulo: 'Cómo llegó',   tipo: 'opcion', opciones: ['Cartel', 'Referido', 'Portal', 'Otro'], defecto: 'Referido' },
      { rotulo: 'Próximo paso', tipo: 'opcion', opciones: ['Llamar', 'Tasar', 'Visitar'], defecto: 'Llamar' },
    ],
  },
  {
    id: 'cambiar-estado',
    titulo: 'Cambiar el estado de una propiedad',
    descripcion: 'Vendida, alquilada, reservada o dada de baja, en el CRM y en los portales.',
    orden: 'Cambiale el estado a esta propiedad, en el CRM y en los portales:',
    campos: [
      { rotulo: 'Propiedad', tipo: 'libre',  placeholder: 'código o dirección' },
      { rotulo: 'Estado',    tipo: 'opcion', opciones: ['Vendida', 'Alquilada', 'Reservada', 'Inactiva', 'Activa'], defecto: 'Vendida' },
      { rotulo: 'Motivo',    tipo: 'libre',  placeholder: 'quién la compró o por qué se baja', opcional: true },
    ],
  },
  {
    id: 'bajar-precio',
    titulo: 'Cambiar el precio',
    descripcion: 'Nuevo precio en la ficha y, si querés, republicado en MercadoLibre.',
    orden: 'Cambiale el precio a esta propiedad:',
    campos: [
      { rotulo: 'Propiedad',    tipo: 'libre',  placeholder: 'código o dirección' },
      { rotulo: 'Nuevo precio', tipo: 'libre',  placeholder: 'monto' },
      { rotulo: 'Moneda',       tipo: 'opcion', opciones: ['USD', 'UYU'], defecto: 'USD' },
      { rotulo: 'Republicar',   tipo: 'opcion', opciones: ['sí, en MercadoLibre', 'no'], defecto: 'sí, en MercadoLibre' },
    ],
  },
  {
    id: 'republicar',
    titulo: 'Republicar en MercadoLibre',
    descripcion: 'Todas, las que vencen o unas puntuales.',
    orden: 'Republicá en MercadoLibre:',
    campos: [
      { rotulo: 'Cuáles',  tipo: 'opcion', opciones: ['todas mis activas', 'las que vencen esta semana', 'estas'], defecto: 'todas mis activas' },
      { rotulo: 'Códigos', tipo: 'libre',  placeholder: 'códigos, si elegiste "estas"', opcional: true },
    ],
  },
  {
    id: 'mandar-fotos',
    titulo: 'Mandar fotos a un cliente',
    descripcion: 'Las fotos de una propiedad, listas para el WhatsApp del cliente.',
    orden: 'Mandale las fotos de esta propiedad al cliente:',
    campos: [
      { rotulo: 'Propiedad', tipo: 'libre',  placeholder: 'código o dirección' },
      { rotulo: 'Cliente',   tipo: 'libre',  placeholder: 'nombre o teléfono' },
      { rotulo: 'Con',       tipo: 'opcion', opciones: ['un mensaje de presentación', 'solo las fotos'], defecto: 'un mensaje de presentación' },
    ],
  },
  {
    id: 'recordatorio',
    titulo: 'Crear un recordatorio',
    descripcion: 'Una tarea con fecha y hora, con o sin cliente.',
    orden: 'Creame este recordatorio:',
    campos: [
      { rotulo: 'Qué',     tipo: 'libre', placeholder: 'qué tenés que hacer' },
      { rotulo: 'Cuándo',  tipo: 'libre', placeholder: 'día y hora' },
      { rotulo: 'Cliente', tipo: 'libre', placeholder: 'nombre, si es de un cliente', opcional: true },
    ],
  },
];

/** Las elecciones por defecto de una plantilla. */
export function eleccionesPorDefecto(p: PlantillaDeGustavo): Elecciones {
  const out: Elecciones = {};
  for (const c of p.campos) {
    if (c.tipo === 'opcion') out[c.rotulo] = c.defecto;
    if (c.tipo === 'multi')  out[c.rotulo] = c.defecto;
  }
  return out;
}

/**
 * El texto que se pega en el composer: la ORDEN primero y una línea por campo, rótulo en
 * negrita de WhatsApp.
 * Lo cerrado va resuelto; lo libre queda como [placeholder]. Precio y Moneda se juntan en
 * una línea ("[monto] USD") para que el agente escriba un solo dato.
 */
export function renderizarPlantilla(p: PlantillaDeGustavo, elecciones: Elecciones = eleccionesPorDefecto(p)): string {
  const lineas: string[] = [];
  const moneda = p.campos.find((c) => c.rotulo === 'Moneda');
  const monedaElegida = moneda && moneda.tipo === 'opcion' ? String(elecciones['Moneda'] ?? moneda.defecto) : '';
  for (const c of p.campos) {
    if (c.rotulo === 'Moneda') continue;
    let valor: string;
    if (c.tipo === 'libre') {
      valor = `[${c.placeholder}]`;
      if (['Precio', 'Nuevo precio', 'Presupuesto', 'Precio que pide'].includes(c.rotulo) && monedaElegida) valor += ` ${monedaElegida}`;
    } else if (c.tipo === 'opcion') {
      valor = String(elecciones[c.rotulo] ?? c.defecto);
    } else {
      const v = elecciones[c.rotulo] ?? c.defecto;
      valor = (Array.isArray(v) ? v : [v]).join(' + ') || 'ninguno';
    }
    lineas.push(`*${c.rotulo}:* ${valor}`);
  }
  return `${p.orden}\n${lineas.join('\n')}`;
}

/** Cuántos datos le quedan por escribir al agente. */
export function camposLibres(p: PlantillaDeGustavo): number {
  return p.campos.filter((c) => c.tipo === 'libre').length;
}
